import { Add } from "@mui/icons-material";
import { Alert, Stack, Tooltip } from "@mui/material";
import { api, types } from "api";
import { CreateUserRequest } from "api/types";
import { InfoSnackBar } from "components/InfoSnackBar";
import { RegisteredTable } from "components/RegisteredTable";
import { StyledButton } from "components/StyledButton";
import { IMPORT_USERS_CSV_TIPS, getUsersFromCSVs } from "helpers/csv/users";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { ERR_REGISTER } from "messages";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { routes } from "routes";
import { CreateAccountPageRegisteredUserAlert } from "./CreateAccountPageRegisteredUserAlert";
import { CreateAccountsPageDialog } from "./CreateAccountsPageDialog";
import { CreateAccountsPageImportCSV } from "./CreateAccountsPageImportCSV";
import { CreateAccountsPageResult } from "./CreateAccountsPageResult";

type AccountDraft = CreateUserRequest & { userId: string };

const createEmptyUser = (): AccountDraft => ({
	userId: Date.now().toString(),
	firstName: "",
	lastName: "",
	email: "",
	role: ["student"],
	password: "",
});

export const CreateAccountsPage = (): JSX.Element => {
	const history = useHistory();
	const { updateLastBreadcrumbs } = useBreadcrumbs();

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [dialogUser, setDialogUser] = useState<AccountDraft>(createEmptyUser());
	const [snackbarMessage, setSnackbarMessage] = useState("");

	const [accounts, setAccounts] = useState<AccountDraft[]>([]);
	const [createdAccounts, setCreatedAccounts] =
		useState<types.RegisterUsersResponse>();

	const addAccountsFromCSV = async (files: File[]): Promise<void> => {
		const { rows, error } = await getUsersFromCSVs(files);

		if (error) {
			setSnackbarMessage(error);
			return;
		}

		setAccounts((curr) => [...curr, ...rows]);
	};

	const addAccount = (account: AccountDraft): void => {
		setAccounts((curr) => [...curr, account]);
	};

	const onDelete = (ids: string[]): void => {
		const idSet = new Set(ids);
		setAccounts((curr) => curr.filter((account) => !idSet.has(account.userId)));
	};

	const goBackToPeoplePage = (): void => {
		history.push(routes._.people.path);
	};

	const createAccounts = (): void => {
		// Ensure the emails are unique
		const emails = new Set(accounts.map((account) => account.email));
		if (emails.size !== accounts.length) {
			setSnackbarMessage("Emails must be unique");
			return;
		}

		api
			.registerUsers(accounts.map(({ userId: _, ...a }) => a))
			.then((res) => {
				if (res.status === 201) {
					setCreatedAccounts(res.data);
					updateLastBreadcrumbs("Created Accounts");
					return;
				}
				else if (res.status === 409) {
					setSnackbarMessage("The email has already been registered");
					return;
				}
				
				setSnackbarMessage(ERR_REGISTER);
			})
			.catch(() => {
				
				setSnackbarMessage(ERR_REGISTER);
			});
	};

	const openDialog = (user: AccountDraft = createEmptyUser()): void => {
		setDialogUser(user);
		setIsDialogOpen(true);
	};

	const editUser = (userId: string): void => {
		const user = accounts.find((u) => u.userId === userId);

		if (user) {
			openDialog(user);
		}
	};

	const closeDialog = (): void => {
		setDialogUser(createEmptyUser());
		setIsDialogOpen(false);
	};

	if (
		createdAccounts &&
		createdAccounts?.new_users &&
		createdAccounts?.new_users.length !== 0
	) {
		return (
			<>
				<CreateAccountsPageResult
					users={createdAccounts?.new_users}
					goBackToPeoplePage={goBackToPeoplePage}
				/>
				<CreateAccountPageRegisteredUserAlert {...createdAccounts} />
			</>
		);
	}

	return (
		<Stack spacing={2}>
			<Alert severity="info">
				{IMPORT_USERS_CSV_TIPS} <br />
				<br />
				Email addresses must be unique.
			</Alert>

			<InfoSnackBar
				isOpen={snackbarMessage !== ""}
				onClose={() => setSnackbarMessage("")}
				message={snackbarMessage}
				severity="error"
			/>

			<CreateAccountsPageDialog
				open={isDialogOpen}
				user={dialogUser}
				onClose={closeDialog}
				onSubmit={addAccount}
			/>

			<RegisteredTable
				users={accounts}
				onDelete={onDelete}
				title="Create Accounts"
				onEditClick={editUser}
			>
				<CreateAccountsPageImportCSV
					addAccountsFromCSV={addAccountsFromCSV}
				/>

				<Tooltip title="Add account">
					<StyledButton
						data-testid="add-account"
						onClick={() => openDialog()}
						aria-label="add account"
						startIcon={<Add />}
						size="medium"
						variant="outlined"
						color="secondary"
						sx={{ minWidth: 180, marginLeft: 1 }}
					>
						Add account
					</StyledButton>
				</Tooltip>
			</RegisteredTable>

			<Stack direction="row-reverse" spacing={1}>
				<StyledButton
					variant="contained"
					color="primary"
					disabled={accounts.length === 0}
					onClick={createAccounts}
				>
					Create accounts
				</StyledButton>
				<StyledButton variant="outlined" onClick={goBackToPeoplePage}>
					Go back to People
				</StyledButton>
			</Stack>
			{createdAccounts && (
				<CreateAccountPageRegisteredUserAlert {...createdAccounts} />
			)}
		</Stack>
	);
};
